import React, { useEffect, forwardRef, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  classNames,
  useGlobalId,
  useUpdateRef,
  useSetState
} from '@zambezi/sdk/common'
import { Select } from '@zambezi/sdk/dropdown-list'
import {
  getError,
  errorOptionsType,
  hintOptionsType,
  getInputProps
} from './zambezi-helpers'
import Label from './Label'

function renderSuggestionDefault ({ label }, { query, getSuggestionValue }) {
  return label || '\u00a0'
}

/**
 * @template T
 * @template Item
 * @type {React.ForwardRefRenderFunction<React.Ref<HTMLInputElement>, import('./interfaces').SelectFieldProps<T, Item>>}
 * @param {import('./interfaces').SelectFieldProps<T, Item>} props
 * @param {React.Ref<HTMLInputElement>} innerRef
 */
function selectField (props, innerRef) {
  const {
    id: propsId,
    name,
    label,
    value: propsValue,
    defaultValue,
    values,
    className,
    rules,
    autoFocus,
    error = '',
    errorOptions,
    errorMessageShown,
    disabled,
    hint,
    hintOptions,
    inputProps = {},
    renderInput = renderInputComp,
    renderSuggestion = renderSuggestionDefault,
    onFocus,
    onChange,
    onBlur,
    errorInline,
    withAsterisk
  } = props

  const errorId = useGlobalId('zb-formidable-error-')
  const globalId = useGlobalId('zb-control-id-')
  const id = propsId || globalId
  const [state, setState] = useSetState({
    isChanged: false,
    isFocused: false,
    value: 'value' in props ? propsValue : defaultValue
  })
  const elementRef = useRef()
  const { isChanged, value } = state

  useEffect(() => {
    setState({ value: propsValue })
  }, [propsValue])

  useUpdateRef(innerRef, elementRef, { ...props })

  const errorMessage = errorMessageShown(error, isChanged)
    ? getError(error, errorOptions, errorId, errorInline)
    : null

  const newInputOptions = {
    value,
    ref: elementRef,
    className: classNames({ 'zb-is-error': !!error }, className),
    onSuggestionSelected: disabled ? noop : handleChange,
    suggestions: values,
    renderInput,
    renderSuggestion,
    getSuggestionValue: ({ value }) => value,
    onBlur: disabled ? noop : handleBlur,
    onFocus: disabled ? noop : handleFocus,
    inputProps: {
      'aria-invalid': !!error,
      'aria-describedby': errorMessage && errorMessage.props.id,
      'aria-required': inputProps.required,
      ...getInputProps(inputProps)
    }
  }

  const inputOptions = { ...inputProps, ...newInputOptions }

  const labelOptions = {
    inputOptions,
    hint,
    hintOptions,
    label,
    error,
    errorOptions
  }

  if (autoFocus) inputOptions.autoFocus = autoFocus
  if (disabled) inputOptions.isDisabled = disabled
  if (id) inputOptions.id = id
  if (error) inputOptions.isError = true
  if (rules) inputOptions.rules = rules

  return (
    <div className='zb-control-wrap'>
      {label ? (
        <Label {...labelOptions}>
          {label} {withAsterisk && <sup className='zb-asterisk'>*</sup>}
        </Label>
      ) : null}
      <div className={classNames('zb-control', { 'is-error': error })}>
        <div className={className}>
          <input
            type='hidden'
            ref={elementRef}
            name={name}
            rules={rules}
            value={value || ''}
          />
          <Select {...inputOptions} />
          {errorMessage}
        </div>
      </div>
    </div>
  )

  function renderInputComp (props) {
    const inputFieldProps = {
      ...getInputProps(inputProps),
      ...getInputProps(props),
      value: selectedLabelForValue(),
      type: props.type || 'text',
      readOnly: true,
      autoComplete: 'off'
    }

    return <input {...inputFieldProps} />
  }

  function handleChange (event, { suggestion: { value } }) {
    setState({
      value,
      isChanged: true
    })
    onChange(name, value)
  }

  function handleBlur () {
    setState({
      isFocused: false
    })
    onBlur(name, value)
  }

  function handleFocus () {
    setState({
      isChanged: false,
      isFocused: true
    })
    onFocus(name, value)
  }

  function selectedLabelForValue () {
    let label = state.value
    const value = label
    values.some(item => {
      if (item.value !== value) return false
      label = item.label
      return true
    })
    return label
  }
}

function noop () {}

/**
 * @template T
 * @template Item
 * @type {React.ForwardRefExoticComponent<import('./interfaces').SelectFieldProps<T, Item> & React.RefAttributes<HTMLInputElement>>}
 */
const SelectField = forwardRef(selectField)

SelectField.displayName = 'SelectField'

SelectField.propTypes = {
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  error: PropTypes.any,
  errorOptions: errorOptionsType,
  errorMessageShown: PropTypes.func,
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  className: PropTypes.string,
  label: PropTypes.node,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  hint: PropTypes.string,
  hintOptions: hintOptionsType,
  values: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  inputProps: PropTypes.object,
  renderInput: PropTypes.func,
  renderSuggestion: PropTypes.func,
  errorInline: PropTypes.bool,
  withAsterisk: PropTypes.bool
}

SelectField.defaultProps = {
  errorOptions: { arrowDirection: 'top' },
  withAsterisk: false,
  onFocus: noop,
  onChange: noop,
  onBlur: noop,
  errorMessageShown: (error, isChanged) => error && !isChanged
}

export default SelectField
